import { useState } from 'react';
import amperaLogo from '../../assests/ampera_logo.png';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { isValidIndonesianPhone } from '../../lib/utils';
import LanguageSwitcher from '../../components/layout/LanguageSwitcher';
import { Button, Field } from '../../components/ui';

export default function Register() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signUp, sendOtp, loading: authLoading } = useAuth();

  // Form state
  const [step, setStep] = useState('details'); // 'details', 'otp'
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otp, setOtp] = useState('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Step 1: Submit name + phone + password, send OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');

    if (!fullName.trim()) {
      setError(t('auth.fullNameRequired'));
      return;
    }

    if (!isValidIndonesianPhone(phone)) {
      setError(t('auth.invalidPhone'));
      return;
    }

    if (password.length < 6) {
      setError(t('auth.passwordTooShort'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return;
    }

    setLoading(true);
    const { error: otpError } = await sendOtp(phone, { shouldCreateUser: true });

    if (otpError) {
      setError(otpError.message || t('auth.otpFailed'));
      setLoading(false);
      return;
    }

    setStep('otp');
    setLoading(false);
  };

  // Step 2: Verify OTP and complete registration
  const handleVerifyAndRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (otp.length < 6) {
      setError(t('auth.otpInvalid'));
      return;
    }

    setLoading(true);
    const { data, error: signUpError } = await signUp(phone, otp, password, {
      fullName,
    });

    if (signUpError) {
      setError(signUpError.message || t('auth.registrationFailed'));
      setLoading(false);
      return;
    }

    setSuccess(true);
    setLoading(false);
  };

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white flex items-center justify-center py-12 px-4">
        <div className="fixed top-4 right-4 z-50">
          <LanguageSwitcher />
        </div>
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t('auth.registrationSuccess')}
            </h2>
            <p className="text-gray-600 mb-6">
              {t('auth.accountReady')}
            </p>
            <Link to="/login" className="btn-primary inline-block">
              {t('auth.goToLogin')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      {/* Language Switcher */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>

      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <img
              src={amperaLogo}
              alt="Ampera"
              className="h-16 mx-auto"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </Link>
          <h1 className="mt-4 text-3xl font-display font-bold text-gray-900">
            {t('auth.createAccount')}
          </h1>
          <p className="mt-2 text-gray-600">
            {t('auth.registerSubtitle')}
          </p>
        </div>

        {/* Register Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Step Indicator */}
          <div className="flex items-center justify-center mb-6 gap-2">
            {['details', 'otp'].map((s, i) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  ['details', 'otp'].indexOf(step) >= i
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}>
                  {i + 1}
                </div>
                {i < 1 && (
                  <div className={`w-8 h-0.5 ${
                    ['details', 'otp'].indexOf(step) > i
                      ? 'bg-primary-600'
                      : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>

          {/* Step 1: Name + Phone + Password */}
          {step === 'details' && (
            <form onSubmit={handleSendOtp} className="space-y-4">
              <Field id="fullName" label={t('auth.fullName')} required>
                <input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder={t('auth.fullNamePlaceholder')}
                  className="input-field"
                  required
                />
              </Field>

              <Field id="phone" label={t('auth.phoneNumber')} required>
                <input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="08xxxxxxxxxx"
                  className="input-field"
                  required
                />
              </Field>

              <Field id="password" label={t('auth.password')} required hint={t('auth.passwordHint')}>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-field"
                  required
                  minLength={6}
                />
              </Field>

              <Field id="confirmPassword" label={t('auth.confirmPassword')} required>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-field"
                  required
                />
              </Field>

              <Button
                type="submit"
                disabled={loading || authLoading}
                className="w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('auth.sendingOtp') : t('auth.sendOtp')}
              </Button>
            </form>
          )}

          {/* Step 2: OTP Verification + Complete Registration */}
          {step === 'otp' && (
            <form onSubmit={handleVerifyAndRegister} className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                {t('auth.otpSentTo', { phone })}
              </p>

              <Field id="otp" label={t('auth.otpCode')}>
                <input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="123456"
                  className="input-field text-center text-2xl tracking-widest"
                  maxLength={6}
                  required
                />
              </Field>

              <Button
                type="submit"
                disabled={loading || otp.length < 6}
                className="w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('auth.creatingAccount') : t('auth.createAccount')}
              </Button>

              <button
                type="button"
                onClick={() => {
                  setStep('details');
                  setOtp('');
                }}
                className="w-full text-primary-600 text-sm hover:underline"
              >
                {t('auth.backToLogin')}
              </button>
            </form>
          )}

          {/* Footer Links */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              {t('auth.haveAccount')}{' '}
              <Link to="/login" className="text-primary-600 hover:underline font-medium">
                {t('auth.signIn')}
              </Link>
            </p>
          </div>

        </div>

        {/* Back to home */}
        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-gray-600 hover:text-gray-900">
            &larr; {t('common.backToHome')}
          </Link>
        </div>
      </div>
    </div>
  );
}
